import { eventBusCommandSaga } from 'features/application/sagas/eventBusCommandSaga';
import { sdkConnectCommandSaga } from 'features/sdk/sdkConnectCommandSaga';
import { onUserMediaFailedCommandSaga } from 'features/sdk/onUserMediaFailedCommandSaga';
import { userMediaFailed } from 'features/user-media/userMediaSlice';
import { fork, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import {
  audioOnlyModeToggleRequested,
  clearContentRequested,
  eventBusCommandReceived,
  interactionTriggered,
  languageChangeRequested,
  pong,
  redirect,
  signalingConnected,
  signalingContentCleared,
  signalingDisconnected,
  signalingReconnected,
} from 'features/application/actions';
import { redirectSaga } from 'features/application/sagas/redirectSaga';
import { connectSignalingSaga } from 'features/application/sagas/connectSignalingSaga';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import { destroySessionSaga } from 'features/application/sagas/destroySessionSaga';
import { prepareReconnectionSaga } from 'features/application/sagas/prepareReconnectionSaga';
import { reconnectionSaga } from 'features/application/sagas/reconnectionSaga';
import { resetReconnectCyclesSaga } from 'features/application/sagas/resetReconnectCyclesSaga';
import {
  globalAudioMuteToggled,
  signalingReconnectionStarted,
} from 'features/application/applicationSlice';
import { refreshConnectionMediaTokensSaga } from 'features/application/sagas/refreshConnectionMediaTokensSaga';
import { refreshIceServersSaga } from 'features/application/sagas/refreshIceServersSaga';
import { interactionBusCommandSaga } from 'features/application/sagas/interactionBusCommandSaga';
import { toggleGlobalAudioMuteSaga } from 'features/application/sagas/toggleGlobalAudioMuteSaga';
import { toggleAudioOnlyModeSaga } from 'features/application/sagas/toggleAudioOnlyModeSaga';
import { changeLanguageSaga } from 'features/application/sagas/changeLanguageSaga';
import { onContentClearedSaga } from 'features/application/sagas/onContentClearedSaga';
import { clearContentSaga } from 'features/application/sagas/clearContentSaga';

const isConnectCommand = (action: unknown): boolean =>
  eventBusCommandReceived.match(action) && action.payload.type === 'connect';

const isNonConnectCommand = (action: unknown): boolean =>
  eventBusCommandReceived.match(action) && action.payload.type !== 'connect';

export function* eventBusCommandWatcher() {
  // takeLeading: later commands must never cancel an in-flight connect.
  // takeEvery: imperative commands must not cancel each other — safe only because no
  // non-connect handler parks on a global take
  yield takeLeading(isConnectCommand, sdkConnectCommandSaga);
  yield takeEvery(isNonConnectCommand, eventBusCommandSaga);
}

export default function* appWatcher() {
  yield takeLatest(languageChangeRequested, changeLanguageSaga);

  yield takeLatest(authorizeUser.fulfilled, connectSignalingSaga);

  yield takeLatest(signalingDisconnected, destroySessionSaga);
  yield takeLatest(signalingReconnectionStarted, prepareReconnectionSaga);
  yield takeLatest(signalingReconnected, reconnectionSaga);
  yield takeLatest(signalingConnected, resetReconnectCyclesSaga);

  yield fork(eventBusCommandWatcher);
  yield takeEvery(userMediaFailed, onUserMediaFailedCommandSaga);

  yield takeLatest(interactionTriggered, interactionBusCommandSaga);

  yield takeEvery(redirect, redirectSaga);
  yield takeEvery(pong, refreshConnectionMediaTokensSaga);
  yield takeEvery(pong, refreshIceServersSaga);

  yield takeEvery(globalAudioMuteToggled, toggleGlobalAudioMuteSaga);

  yield takeLatest(audioOnlyModeToggleRequested, toggleAudioOnlyModeSaga);

  yield takeLatest(clearContentRequested, clearContentSaga);
  yield takeLatest(signalingContentCleared, onContentClearedSaga);
}
